input,
select,
textarea {
  padding: 0;
  margin: 0;
  background-color: unset;
  border: unset;
  color: unset;
  font: unset;
  outline: unset;
}

:where(button, a) {
  all: unset;
  display: inline-flex;
  align-items: center;
  user-select: none;
  touch-action: manipulation;
  cursor: pointer;
}

button:disabled {
  pointer-events: none;
}

*,
::after,
::before {
  box-sizing: border-box;
}

:host {
  /**
   * @prop --ch-combo-box-group__expandable-button-size:
   * Specifies the expandable button size in the group items.
   * @default 0.875em
   */
  --ch-combo-box-group__expandable-button-size: 0.875em;
  /**
   * @prop --ch-combo-box-group__expandable-button-image-size:
   * Specifies the image size of the expandable button size in the group items. 
   * @default 100%
   */
  --ch-combo-box-group__expandable-button-image-size: 0.875em;
  /**
   * @prop --ch-combo-box-item__image-size:
   * Specifies the box size that contains an image in the items. For example,
   * the box for the startImgSrc and endImgSrc
   * @default 0.875em
   */
  --ch-combo-box-item__image-size: 0.875em;
  /**
   * @prop --ch-combo-box-item__background-image-size:
   * Specifies the image size of the items. For example, the image size for the
   * startImgSrc and endImgSrc
   * @default 100%
   */
  --ch-combo-box-item__background-image-size: 100%;
  /**
   * @prop --ch-combo-box__picker:
   * Specifies the image of the combo-box's picker.
   * @default url('data:image/svg+xml,<svg width="8" height="12" viewBox="0 0 8 12" xmlns="http://www.w3.org/2000/svg"><path d="M4.16669 0.666626L7.66669 4.66663H0.666687L4.16669 0.666626ZM4.16669 11.3333L0.666687 7.33329H7.66669L4.16669 11.3333Z"/></svg>')
   */
  --ch-combo-box__picker: url('data:image/svg+xml,<svg width="8" height="12" viewBox="0 0 8 12" xmlns="http://www.w3.org/2000/svg"><path d="M4.16669 0.666626L7.66669 4.66663H0.666687L4.16669 0.666626ZM4.16669 11.3333L0.666687 7.33329H7.66669L4.16669 11.3333Z"/></svg>');
  /**
   * @prop --ch-combo-box__picker-collapsed:
   * Specifies the image of the combo-box's picker when it is collapsed.
   * @default var(--ch-combo-box__picker)
   */
  --ch-combo-box__picker-collapsed: var(
    --ch-combo-box__picker
  );
  /**
   * @prop --ch-combo-box__picker-expanded:
   * Specifies the image of the combo-box's picker when it is expanded.
   * @default var(--ch-combo-box__picker)
   */
  --ch-combo-box__picker-expanded: var(
    --ch-combo-box__picker
  );
  /**
   * @prop --ch-combo-box__picker-color:
   * Specifies the color of the combo-box's picker.
   * @default currentColor
   */
  --ch-combo-box__picker-color: currentColor;
  /**
   * @prop --ch-combo-box__picker-size:
   * Specifies the box size that contains the combo-box's picker.
   * @default 0.875em
   */
  --ch-combo-box__picker-size: 0.875em;
  /**
   * @prop --ch-combo-box__picker-mask-size:
   * Specifies the image size of the combo-box's picker.
   * @default 100%
   */
  --ch-combo-box__picker-image-size: 100%;
  /**
   * @prop --ch-combo-box__placeholder-color:
   * Define the placeholder color when the combo-box does not have a value set.
   * (currentColor by default)
   */
  --ch-combo-box__placeholder-color: currentColor;
  /**
   * @prop --ch-combo-box__popover-max-block-size:
   * Specifies the maximum block size of the popover. Only px values are supported.
   * @default auto
   */
  --ch-combo-box__popover-max-block-size: auto;
  /**
   * @prop --ch-combo-box__popover-max-inline-size:
   * Specifies the maximum inline size of the popover. Only px values are supported.
   * @default auto
   */
  --ch-combo-box__popover-max-inline-size: auto;
  /**
   * @prop --ch-combo-box-item-gap:
   * Specifies the spacing between the images, text and the expandable button
   * on items.
   * @default 0px
   */
  --ch-combo-box-item-gap: 0px;
  /**
   * @prop --ch-combo-box-separation:
   * Specifies the separation between the combo-box and the displayed popover.
   * @default 0px
   */
  --ch-combo-box-separation: 0px;
  /**
   * @prop --ch-combo-box-separation-x:
   * Specifies the separation between the combo-box and the popover displayed
   * on the x-axis.
   * @default var(--ch-combo-box-separation)
   */
  --ch-combo-box-separation-x: var(--ch-combo-box-separation);
  /**
   * @prop --ch-combo-box-separation-y:
   * Specifies the separation between the combo-box and the popover displayed
   * on the y-axis.
   * @default var(--ch-combo-box-separation)
   */
  --ch-combo-box-separation-y: var(--ch-combo-box-separation);
  display: inline-grid;
  grid-template-columns: 1fr max-content;
  grid-template-rows: max-content 1fr;
  align-items: center;
}
:host::after {
  grid-row: 1/3;
  grid-column: 2;
  inline-size: var(--ch-combo-box__picker-size);
  block-size: var(--ch-combo-box__picker-size);
  -webkit-mask: var(--ch-combo-box__picker-collapsed) 50% 50%/var(--ch-combo-box__picker-image-size) var(--ch-combo-box__picker-image-size) no-repeat;
  margin-inline-start: var(--ch-combo-box-item-gap);
  background-color: var(--ch-combo-box__picker-color);
  pointer-events: none;
}

:host(.ch-combo-box--expanded)::after {
  -webkit-mask-image: var(--ch-combo-box__picker-expanded);
}

:host(.ch-combo-box--normal) {
  cursor: pointer;
  user-select: none;
}
:host(.ch-combo-box--normal)::after {
  content: "";
}

:host(.ch-combo-box--suggest) {
  --ch-combo-box__picker-size: 0px;
  cursor: text;
}

:host(.ch-disabled) {
  pointer-events: none;
}

.input-container {
  grid-row: 2/3;
  outline: unset;
}

.invisible-text {
  visibility: hidden;
  pointer-events: none;
  block-size: 0;
  overflow: hidden;
}

select {
  all: unset;
  display: grid;
  overflow: hidden;
  grid-row: 2/3;
}

.value {
  display: inline-grid;
  align-items: center;
  inline-size: 100%;
}
.value--readonly {
  pointer-events: none;
  text-overflow: ellipsis;
}
.value::placeholder {
  color: var(--ch-combo-box__placeholder-color);
}

ch-popover {
  --ch-popover-max-inline-size: var(--ch-combo-box__popover-max-inline-size);
  --ch-popover-max-block-size: var(--ch-combo-box__popover-max-block-size);
  --ch-popover-separation-x: var(--ch-combo-box-separation-x);
  --ch-popover-separation-y: var(--ch-combo-box-separation-y);
  cursor: auto;
}
ch-popover:not(.hydrated) {
  opacity: 0;
}

.leaf,
.group__header,
.group__header-caption,
.input-container {
  display: flex;
  gap: var(--ch-combo-box-item-gap);
  align-items: center;
  flex: 1;
}

.group {
  display: grid;
  grid-template-rows: max-content 1fr;
}
.group--expandable::before {
  content: "";
  inline-size: var(--ch-combo-box-group__expandable-button-size);
  block-size: var(--ch-combo-box-group__expandable-button-size);
  background-color: currentColor;
  -webkit-mask: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" fill="none"><path d="M16.5 5L8.7 12.7L1 5" stroke="%23000" stroke-width="1.2" stroke-linecap="round"/></svg>') 50% 50%/var(--ch-combo-box-group__expandable-button-image-size) var(--ch-combo-box-group__expandable-button-image-size) no-repeat;
}
.group--collapsed::before {
  transform: rotate(-90deg);
}
.ch-popover-rtl .group--collapsed::before {
  transform: rotate(90deg);
}
.group__content {
  display: grid;
  grid-auto-rows: max-content;
}
.group__content--collapsed {
  display: none;
}

.img--start::before,
.img--end::after {
  content: "";
  display: block;
  inline-size: var(--ch-combo-box-item__image-size);
  block-size: var(--ch-combo-box-item__image-size);
  min-inline-size: var(--ch-combo-box-item__image-size);
  pointer-events: none;
}

.img--start {
  --ch-combo-box-item-start-img: var(--ch-start-img--base);
}
.img--start.selected.start-img--selected {
  --ch-combo-box-item-start-img: var(--ch-start-img--selected);
}
.group__header:hover > .img--start.start-img--hover {
  --ch-combo-box-item-start-img: var(--ch-start-img--hover);
}
.group__header:active > .img--start.start-img--active {
  --ch-combo-box-item-start-img: var(--ch-start-img--active);
}
:host(:focus) .img--start.input-container.start-img--focus {
  --ch-combo-box-item-start-img: var(--ch-start-img--focus);
}
:host(:not(.ch-disabled):hover) .img--start.input-container.start-img--hover {
  --ch-combo-box-item-start-img: var(--ch-start-img--hover);
}
.img--start.disabled.start-img--disabled, .disabled > .img--start.start-img--disabled, :host(.ch-disabled) .img--start.start-img--disabled {
  --ch-combo-box-item-start-img: var(--ch-start-img--disabled);
}

.img--end {
  --ch-combo-box-item-end-img: var(--ch-end-img--base);
}
.img--end::after {
  margin-inline-start: auto;
}
.img--end.selected.end-img--selected {
  --ch-combo-box-item-end-img: var(--ch-end-img--selected);
}
.group__header:hover > .img--end.end-img--hover {
  --ch-combo-box-item-end-img: var(--ch-end-img--hover);
}
.group__header:active > .img--end.end-img--active {
  --ch-combo-box-item-end-img: var(--ch-end-img--active);
}
.img--end.disabled.end-img--disabled, .disabled > .img--end.end-img--disabled {
  --ch-combo-box-item-end-img: var(--ch-end-img--disabled);
}

.start-img-type--background::before {
  background: no-repeat center/var(--ch-combo-box-item__background-image-size) var(--ch-combo-box-item-start-img);
}

.end-img-type--background::after {
  background: no-repeat center/var(--ch-combo-box-item__background-image-size) var(--ch-combo-box-item-end-img);
}

.start-img-type--mask::before {
  -webkit-mask: no-repeat center/var(--ch-combo-box-item__background-image-size) var(--ch-combo-box-item-start-img);
  background-color: currentColor;
}

.end-img-type--mask::after {
  -webkit-mask: no-repeat center/var(--ch-combo-box-item__background-image-size) var(--ch-combo-box-item-end-img);
  background-color: currentColor;
}